/**
 * MiniProjectMilestoneEditTray — right-edge sliding tray for milestone
 * create + edit. Replaces the inline AddRow / EditRow that lived in
 * MiniProjectMilestoneList. Skip flagged on 2026-05-27 that inline
 * create/edit didn't lay out cleanly at narrow widths (the Summary
 * sub-tab sits inside the MP detail magazine column, so the milestone
 * widget routinely renders ~360–420px wide). Trays solve this — they
 * float over the parent at a fixed 560px regardless of host width.
 *
 * Modeled on MiniProjectBomItemEditTray (DEV-1110) for visual + API
 * consistency with the other MP-detail trays
 * (MiniProjectTaskEditTray, GoalEditTray, MiniProjectBomItemEditTray).
 *
 * Mode-discriminated props:
 *   - mode="create" : POST /api/projectmilestones/mini?miniProjectGuid=...
 *   - mode="edit"   : PUT  /api/projectmilestones/{id} (+ optional DELETE)
 *
 * Field set:
 *   - Name           — required
 *   - Target date    — optional (DateOnly; ISO YYYY-MM-DD)
 *   - Actual date    — optional; presence flips status to "met"
 *
 * Edit mode adds:
 *   - Delete button (danger variant) — full removal, not soft-archive
 *   - "Clear actual" affordance — the PUT contract uses clearActualDate=true
 *     because a null in actualDate alone means "don't change" on partial
 *     updates (see UpdateProjectMilestoneRequest in the API).
 *
 * Refresh: createMiniProjectMilestone + updateProjectMilestone already
 * dispatch EVT_MINI_PROJECT_MUTATED; delete does not, so we dispatch
 * here ourselves with the known miniProjectGuid (same pattern as the
 * list's old handleDelete).
 *
 * Strings stay hardcoded English to match the current state of
 * MiniProjectMilestoneList.tsx — that file hasn't been localized yet.
 * Both files should be wrapped in useAppTranslation in a single pass
 * once the milestone widget feature is stable.
 */

import React, { useCallback, useState } from "react";
import {
  createMiniProjectMilestone,
  updateProjectMilestone,
  deleteProjectMilestone,
  type ProjectMilestoneResponse,
  type CreateMilestoneRequest,
  type UpdateMilestoneRequest,
} from "../../api/projectMilestones";
import { dispatchMiniProjectMutated } from "../../api/miniProjects";
import { Tray } from "../Tray/Tray";
import styles from "./MiniProjectMilestoneEditTray.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

type Props =
  | {
      mode: "edit";
      open: boolean;
      milestone: ProjectMilestoneResponse | null;
      miniProjectGuid: string;
      onClose: () => void;
      onMutated: () => void;
    }
  | {
      mode: "create";
      open: boolean;
      miniProjectGuid: string;
      onClose: () => void;
      onMutated: () => void;
    };

/* ── Inline icons ──────────────────────────────────────────────── */

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

/* ── Component ─────────────────────────────────────────────────── */

const MiniProjectMilestoneEditTray: React.FC<Props> = (props) => {
  const { mode, open, onClose, onMutated, miniProjectGuid } = props;

  // Mode-discriminated initial values. The parent should remount this
  // component on each open by keying on milestone.id (edit) or a create
  // counter — that's how useState initializers fire fresh each time.
  const milestone = mode === "edit" ? props.milestone : null;

  // ── Local state ──
  const [name, setName] = useState<string>(milestone?.name ?? "");
  const [targetDate, setTargetDate] = useState<string>(milestone?.targetDate ?? "");
  const [actualDate, setActualDate] = useState<string>(milestone?.actualDate ?? "");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Save (branches on mode) ──
  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Milestone name is required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (mode === "create") {
        const req: CreateMilestoneRequest = {
          name: trimmedName,
          targetDate: targetDate || null,
          actualDate: actualDate || null,
          sortOrder: 0,
        };
        await createMiniProjectMilestone(miniProjectGuid, req);
      } else if (milestone) {
        const req: UpdateMilestoneRequest = {};

        if (trimmedName !== milestone.name) req.name = trimmedName;

        const draftTarget = targetDate || null;
        if (draftTarget !== (milestone.targetDate ?? null)) {
          req.targetDate = draftTarget;
        }

        // Three-state actual-date logic:
        //   draft set, original empty/diff → req.actualDate = draftActual
        //   draft empty, original set      → req.clearActualDate = true
        //   no change                      → omit
        const draftActual = actualDate || null;
        const origActual = milestone.actualDate ?? null;
        if (draftActual !== origActual) {
          if (draftActual === null) {
            req.clearActualDate = true;
          } else {
            req.actualDate = draftActual;
          }
        }

        // No-op edit — close without round-tripping the API.
        if (Object.keys(req).length === 0) {
          onClose();
          return;
        }

        await updateProjectMilestone(milestone.id, req);
      }
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [
    mode,
    miniProjectGuid,
    milestone,
    name,
    targetDate,
    actualDate,
    onMutated,
    onClose,
  ]);

  // ── Delete (edit only) ──
  const handleDelete = useCallback(async () => {
    if (!milestone) return;
    if (!window.confirm(`Delete milestone "${milestone.name}"?`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteProjectMilestone(milestone.id);
      // DELETE returns 204 with no body, so the API helper can't
      // dispatch on its own. Dispatch here with the known MP guid.
      dispatchMiniProjectMutated(miniProjectGuid);
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [milestone, miniProjectGuid, onMutated]);

  // ── Clear actual date (edit only convenience) ──
  // Surfaces as an explicit affordance so the Lead doesn't have to
  // know that "blank + save" un-marks. Keeps the field input matching
  // <input type="date"> semantics (which can't easily distinguish
  // "user blanked it" from "user never set it").
  const handleClearActual = useCallback(() => {
    setActualDate("");
  }, []);

  // ── Title ──
  // Edit body is gated on milestone != null so the prior name doesn't
  // ghost into the title while the Tray animates closed.
  const bodyOk = mode === "create" || milestone != null;
  const trayTitle =
    mode === "create"
      ? "Add milestone"
      : milestone
        ? `Edit milestone: ${milestone.name}`
        : "Edit milestone";

  return (
    <Tray open={open} onClose={onClose} title={trayTitle}>
      {bodyOk && (
        <>
          <Tray.Body className={styles.body}>
            <div className={styles.row}>
              <label className={styles.label}>Name</label>
              <input
                className={styles.input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                placeholder="e.g. Permits secured"
                autoFocus={mode === "create"}
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                Target date
                <span className={styles.opt}>optional</span>
              </label>
              <input
                className={styles.input}
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                disabled={busy}
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                Actual date
                <span className={styles.opt}>set to mark as met</span>
              </label>
              <div className={styles.actualGroup}>
                <input
                  className={styles.input}
                  type="date"
                  value={actualDate}
                  onChange={(e) => setActualDate(e.target.value)}
                  disabled={busy}
                />
                {mode === "edit" && milestone?.actualDate && (
                  <button
                    type="button"
                    className={styles.clearBtn}
                    onClick={handleClearActual}
                    disabled={busy}
                    title="Clear the actual date (un-mark as met)"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className={styles.hint}>
                Setting an actual date marks the milestone as met. Clear it
                to un-mark.
              </div>
            </div>

            {error && <div className={styles.errorMsg}>{error}</div>}
          </Tray.Body>

          <Tray.Footer className={styles.footer}>
            {mode === "edit" && milestone && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnDanger}`}
                onClick={handleDelete}
                disabled={busy}
              >
                <TrashIcon /> Delete
              </button>
            )}
            <button
              type="button"
              className={styles.btn}
              onClick={onClose}
              disabled={busy}
              style={mode === "create" ? { marginInlineStart: "auto" } : undefined}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleSave}
              disabled={busy || !name.trim()}
            >
              {busy ? "Saving…" : mode === "create" ? "Add" : "Save"}
            </button>
          </Tray.Footer>
        </>
      )}
    </Tray>
  );
};

export default MiniProjectMilestoneEditTray;
